import { isAllowedUri, type LinkOptions } from '@tiptap/extension-link'
import { richTextEditorLinkValueRegex } from '@awork/_shared/static-data/regular-expressions'
import { hasUnsafeUrlCharacters, isAppRelativeUrl } from '@awork/_shared/functions/app-relative-url'

/** Additional rich-text protocols; TipTap also accepts its built-in safe protocols. */
export const DOCUMENT_LINK_PROTOCOLS: LinkOptions['protocols'] = [
  'mailto',
  'tel',
  { scheme: 'smb', optionalSlashes: true }
]

const VALIDATION_ORIGIN = 'https://awork.invalid'

/**
 * Validates persisted and pasted link tokens without converting bare file names or domains into links.
 * Explicit link-entry interfaces can normalize their input before calling this function.
 * @param {string} value Raw link token.
 * @returns {boolean} Whether the target uses a supported protocol or a safe app-relative route.
 */
export function isSafeDocumentLink(value: string): boolean {
  if (!value || !isAllowedUri(value, DOCUMENT_LINK_PROTOCOLS) || !richTextEditorLinkValueRegex.test(value)) {
    return false
  }

  if (value.startsWith('/') && !value.startsWith('//')) {
    return isAppRelativeUrl(value, VALIDATION_ORIGIN)
  }

  try {
    const resolved = new URL(value, VALIDATION_ORIGIN)

    return Boolean(resolved.hostname || resolved.pathname || resolved.search)
  } catch {
    return false
  }
}

/**
 * Normalizes explicit link-entry input while preserving strict paste and parser validation.
 * @param {string} value User-entered URL.
 * @returns {string | undefined} URL with a protocol when needed, or undefined for unsafe characters and whitespace.
 */
export function normalizeDocumentLinkInput(value: string): string | undefined {
  if (hasUnsafeUrlCharacters(value)) {
    return undefined
  }

  const href = value.trim()

  if (!href || /\s/.test(href)) {
    return undefined
  }

  if (href.startsWith('//')) {
    return `https:${href}`
  }

  if (!href.startsWith('/') && !/^[a-z][a-z0-9+.-]*:/i.test(href)) {
    return `https://${href}`
  }

  return href
}
